import { useState, useEffect, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { supabase } from "../supabaseClient"
import { getIframeAccessCounts, getIframeAccessDetails, getUserId, getOrgUserIds } from "../API"
import { BarChart3, Users, FolderOpen, Search, ChevronDown, ChevronRight, Shirt, Layers, Globe, MapPin, UserPlus } from "lucide-react"
import axios from "axios"

const ADMIN_EMAIL = "davidsonjds@gmail.com"

export function isAdmin() {
    const internal = (() => {
        try {
            const d = JSON.parse(localStorage.getItem("user_data") || "null")
            return !!(d && d.isAdmin)
        } catch {
            return false
        }
    })()
    return localStorage.getItem("user_email") === ADMIN_EMAIL || internal
}

interface MemberStats {
    client_id: string
    name: string
    role: string | null
    projects: number
    modelagens: number
    categories: number
    accesses: number
}

interface StoreGroup {
    email: string | null
    members: MemberStats[]
    totalProjects: number
    totalModelagens: number
    totalCategories: number
    totalAccesses: number
}

export function AdminDashboard({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
    const [loading, setLoading] = useState(true)
    const [storeGroups, setStoreGroups] = useState<StoreGroup[]>([])
    const [summary, setSummary] = useState({ totalStores: 0, totalMembers: 0, totalProjects: 0, totalModelagens: 0, totalCategories: 0, totalAccesses: 0, roles: {} as Record<string, number> })
    const [search, setSearch] = useState("")
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [accessDetailOpen, setAccessDetailOpen] = useState(false)
    const [accessDetails, setAccessDetails] = useState<any[]>([])
    const [accessDetailLoading, setAccessDetailLoading] = useState(false)
    const [myStats, setMyStats] = useState<{ projects: number; modelagens: number; categories: number; accesses: number } | null>(null)

    const isGlobalAdmin = isAdmin()

    // ── Criação de usuário (membro da mesma loja) — apenas admin ──
    const canManageUsers = isAdmin()
    const [userForm, setUserForm] = useState({ email: "", name: "", password: "", role: "designer" })
    const [userMsg, setUserMsg] = useState<{ type: string; text: string } | null>(null)
    const [creatingUser, setCreatingUser] = useState(false)

    async function createUser(e: React.FormEvent) {
        e.preventDefault()
        setUserMsg(null)
        if (!userForm.email || !userForm.password) {
            setUserMsg({ type: "error", text: "Preencha email e senha" })
            return
        }
        setCreatingUser(true)
        try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000/api"
            const token = localStorage.getItem("token")
            const response = await axios.post(`${BACKEND_URL}/users`, {
                email: userForm.email,
                name: userForm.name,
                password: userForm.password,
                role: userForm.role,
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 8000,
            })
            if (!response.data.success) throw new Error(response.data.message || "Erro ao criar usuário")
            setUserMsg({ type: "success", text: `Usuário ${response.data.data.userEmail} criado com sucesso!` })
            setUserForm({ email: "", name: "", password: "", role: "designer" })
            if (isGlobalAdmin) loadGlobal(); else loadMyData()
        } catch (err: any) {
            const msg =
                (err?.response?.data?.message) ||
                (err?.code === "ECONNABORTED" ? "Tempo de resposta excedido." : "Erro ao criar usuário.")
            setUserMsg({ type: "error", text: msg })
        } finally {
            setCreatingUser(false)
        }
    }

    useEffect(() => {
        if (!open) return
        load()
    }, [open])

    async function load() {
        setLoading(true)
        try {
            if (isGlobalAdmin) {
                await loadGlobal()
            } else {
                await loadMyData()
            }
        } catch (e) {
            console.error("AdminDashboard load error:", e)
        }
        setLoading(false)
    }

    // ── Admin global: carrega tudo ──
    async function loadGlobal() {
        const { data: allClients } = await supabase
            .from("clients")
            .select("client_id, name, role, email")

        if (!allClients || !allClients.length) return

        const emailGroups = new Map<string, typeof allClients>()
        const noEmail: typeof allClients = []
        for (const c of allClients) {
            if (c.email) {
                const arr = emailGroups.get(c.email) || []
                arr.push(c)
                emailGroups.set(c.email, arr)
            } else {
                noEmail.push(c)
            }
        }
        for (const c of noEmail) {
            emailGroups.set(`_single_${c.client_id}`, [c])
        }

        const orgEntries = Array.from(emailGroups.entries()).map(([email, members]) => ({
            email: email.startsWith("_single_") ? null : email,
            clientIds: members.map(m => m.client_id),
            membersMeta: members,
        }))

        const fetchAllUserIds = async (table: string, extraFilter?: (q: any) => any) => {
            const ids: string[] = []
            let from = 0
            const pageSize = 1000
            while (true) {
                let q = supabase.from(table).select("user_id").range(from, from + pageSize - 1)
                if (extraFilter) q = extraFilter(q)
                const { data, error } = await q
                if (error || !data || !data.length) break
                data.forEach((r: any) => { if (r.user_id) ids.push(r.user_id) })
                if (data.length < pageSize) break
                from += pageSize
            }
            return ids
        }

        const [projIds, modelIds, catIds] = await Promise.all([
            fetchAllUserIds("simulator"),
            fetchAllUserIds("3ds", (q: any) => q.is("setup_data", null)),
            fetchAllUserIds("categories_new"),
        ])

        const accessCounts: Record<string, number> = await getIframeAccessCounts()

        const projByUser = new Map<string, number>()
        projIds.forEach((uid) => { projByUser.set(uid, (projByUser.get(uid) || 0) + 1) })
        const modelByUser = new Map<string, number>()
        modelIds.forEach((uid) => { modelByUser.set(uid, (modelByUser.get(uid) || 0) + 1) })
        const catByUser = new Map<string, number>()
        catIds.forEach((uid) => { catByUser.set(uid, (catByUser.get(uid) || 0) + 1) })

        const roles: Record<string, number> = {}
        const groups: StoreGroup[] = orgEntries.map(org => {
            const members: MemberStats[] = org.membersMeta.map(m => {
                const r = m.role || "sem_cargo"
                roles[r] = (roles[r] || 0) + 1
                return {
                    client_id: m.client_id,
                    name: m.name || "Sem nome",
                    role: m.role,
                    projects: projByUser.get(m.client_id) || 0,
                    modelagens: modelByUser.get(m.client_id) || 0,
                    categories: catByUser.get(m.client_id) || 0,
                    accesses: accessCounts[m.client_id] || 0,
                }
            })
            return {
                email: org.email,
                members,
                totalProjects: members.reduce((s, m) => s + m.projects, 0),
                totalModelagens: members.reduce((s, m) => s + m.modelagens, 0),
                totalCategories: members.reduce((s, m) => s + m.categories, 0),
                totalAccesses: members.reduce((s, m) => s + m.accesses, 0),
            }
        }).sort((a, b) => b.totalProjects - a.totalProjects)

        setStoreGroups(groups)
        setSummary({
            totalStores: groups.length,
            totalMembers: allClients.length,
            totalProjects: projIds.length,
            totalModelagens: modelIds.length,
            totalCategories: catIds.length,
            totalAccesses: Object.values(accessCounts).reduce((s: number, v: number) => s + v, 0),
            roles,
        })
    }

    // ── Usuario normal: carrega só os dados da própria loja ──
    async function loadMyData() {
        const myId = getUserId()
        if (!myId) return

        const orgIds = await getOrgUserIds(myId)
        const ids = orgIds && orgIds.length ? orgIds : [myId]

        const fetchCount = async (table: string, extraFilter?: (q: any) => any) => {
            let count = 0
            for (const uid of ids) {
                let q = supabase.from(table).select("id", { count: "exact", head: true }).eq("user_id", uid)
                if (extraFilter) q = extraFilter(q)
                const { count: c } = await q
                if (c) count += c
            }
            return count
        }

        const [projects, modelagens, categories] = await Promise.all([
            fetchCount("simulator"),
            fetchCount("3ds", (q: any) => q.is("setup_data", null)),
            fetchCount("categories_new"),
        ])

        const accessCounts: Record<string, number> = await getIframeAccessCounts()
        const myAccesses = ids.reduce((s: number, uid: string) => s + (accessCounts[uid] || 0), 0)

        setMyStats({ projects, modelagens, categories, accesses: myAccesses })
        setSummary({
            totalStores: 1,
            totalMembers: ids.length,
            totalProjects: projects,
            totalModelagens: modelagens,
            totalCategories: categories,
            totalAccesses: myAccesses,
            roles: {},
        })
    }

    const filtered = useMemo(() => {
        if (!search.trim()) return storeGroups
        const q = search.toLowerCase()
        return storeGroups.filter(g =>
            (g.email && g.email.toLowerCase().includes(q)) ||
            g.members.some(m => m.name.toLowerCase().includes(q))
        )
    }, [storeGroups, search])

    function toggleExpand(email: string) {
        setExpanded(prev => {
            const next = new Set(prev)
            if (next.has(email)) next.delete(email)
            else next.add(email)
            return next
        })
    }

    async function openAccessDetail(ownerIds: string[]) {
        setAccessDetailOpen(true)
        setAccessDetailLoading(true)
        const allDetails: any[] = []
        for (const oid of ownerIds) {
            const details = await getIframeAccessDetails(oid)
            allDetails.push(...details)
        }
        allDetails.sort((a, b) => new Date(b.accessed_at).getTime() - new Date(a.accessed_at).getTime())
        setAccessDetails(allDetails)
        setAccessDetailLoading(false)
    }

    const locationBreakdown = useMemo(() => {
        const map = new Map<string, { count: number; city: string; region: string; country: string }>()
        accessDetails.forEach((d) => {
            const key = [d.city, d.region, d.country].filter(Boolean).join(", ") || "Desconhecido"
            const existing = map.get(key) || { count: 0, city: d.city, region: d.region, country: d.country }
            existing.count++
            map.set(key, existing)
        })
        return Array.from(map.values()).sort((a, b) => b.count - a.count)
    }, [accessDetails])

    const title = isGlobalAdmin ? "Dashboard Admin" : "Meus Dados"

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col bg-background text-primary">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        {title}
                    </DialogTitle>
                </DialogHeader>

                {/* Criar usuário — apenas admin */}
                {canManageUsers && (
                    <form onSubmit={createUser} className="border border-white/10 rounded-lg p-4 space-y-3 mb-4">
                        <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider flex items-center gap-2">
                            <UserPlus className="h-4 w-4" /> Criar Usuário
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                                type="email"
                                placeholder="Email do usuário"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/30"
                                required
                            />
                            <input
                                type="text"
                                placeholder="Nome"
                                value={userForm.name}
                                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/30"
                            />
                            <input
                                type="password"
                                placeholder="Senha"
                                value={userForm.password}
                                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/30"
                                required
                            />
                            <select
                                value={userForm.role}
                                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                            >
                                <option value="designer">Designer</option>
                                <option value="vendedor">Vendedor</option>
                                <option value="gestao">Gestão</option>
                            </select>
                        </div>
                        {userMsg && (
                            <p className={`text-sm ${userMsg.type === "success" ? "text-green-500" : "text-red-500"}`}>
                                {userMsg.text}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={creatingUser}
                            className="rounded-lg px-4 py-2 text-sm font-medium bg-primary text-background hover:bg-primary/90 disabled:opacity-50"
                        >
                            {creatingUser ? "Criando..." : "Criar usuário"}
                        </button>
                    </form>
                )}

                {loading ? (
                    <div className="flex-1 flex items-center justify-center py-12 text-primary/50">Carregando...</div>
                ) : (
                    <div className="flex-1 overflow-y-auto space-y-5 pr-2">
                        {/* Summary cards */}
                        <div className={`grid gap-3 ${isGlobalAdmin ? "grid-cols-2 lg:grid-cols-5" : "grid-cols-2 lg:grid-cols-4"}`}>
                            {isGlobalAdmin && (
                                <SummaryCard icon={<Users className="h-4 w-4" />} label="Lojas" value={summary.totalStores} />
                            )}
                            {!isGlobalAdmin && (
                                <SummaryCard icon={<Users className="h-4 w-4" />} label="Membros" value={summary.totalMembers} />
                            )}
                            <SummaryCard icon={<FolderOpen className="h-4 w-4" />} label="Projetos" value={summary.totalProjects} />
                            <SummaryCard icon={<Shirt className="h-4 w-4" />} label="Modelagens" value={summary.totalModelagens} />
                            <SummaryCard icon={<Layers className="h-4 w-4" />} label="Categorias" value={summary.totalCategories} />
                            <SummaryCard
                                icon={<Globe className="h-4 w-4" />}
                                label="Acessos iframe"
                                value={summary.totalAccesses}
                                onClick={summary.totalAccesses > 0 ? () => {
                                    const myId = getUserId()
                                    if (myId) openAccessDetail(isGlobalAdmin ? [] : [myId])
                                } : undefined}
                            />
                        </div>

                        {/* Roles breakdown — admin only */}
                        {isGlobalAdmin && Object.keys(summary.roles).length > 0 && (
                            <div className="border border-white/10 rounded-lg p-4">
                                <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-3">Distribuicao de Cargos</p>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(summary.roles).map(([role, count]) => (
                                        <span key={role} className="rounded-full px-3 py-1 text-xs font-medium bg-white/10 text-primary/80">
                                            {role}: {count}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Search + Store groups — admin only */}
                        {isGlobalAdmin && (
                            <>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/40" />
                                    <input
                                        type="text"
                                        placeholder="Buscar loja ou usuario..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-white/10 bg-white/5 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/30"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider">
                                        Lojas ({filtered.length})
                                    </p>
                                    {filtered.map((store) => {
                                        const key = store.email || store.members[0].client_id
                                        const isExpanded = expanded.has(key)
                                        const isSingle = store.members.length === 1
                                        return (
                                            <div key={key} className="border border-white/10 rounded-lg overflow-hidden">
                                                <div
                                                    className={`flex items-center gap-3 px-4 py-3 bg-white/5 ${!isSingle ? "cursor-pointer hover:bg-white/10" : ""} transition-colors`}
                                                    onClick={() => !isSingle && toggleExpand(key)}
                                                >
                                                    {!isSingle ? (
                                                        isExpanded
                                                            ? <ChevronDown className="h-4 w-4 text-primary/50 flex-shrink-0" />
                                                            : <ChevronRight className="h-4 w-4 text-primary/50 flex-shrink-0" />
                                                    ) : <div className="w-4 flex-shrink-0" />}

                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-primary/90 truncate">
                                                            {store.email || store.members[0].name}
                                                        </div>
                                                        {!isSingle && (
                                                            <div className="text-xs text-primary/40">
                                                                {store.members.length} membros
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex gap-4 text-right flex-shrink-0">
                                                        <div>
                                                            <div className="text-sm font-mono font-bold text-primary/90">{store.totalProjects}</div>
                                                            <div className="text-[10px] text-primary/40 uppercase">Projetos</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-mono font-bold text-primary/90">{store.totalModelagens}</div>
                                                            <div className="text-[10px] text-primary/40 uppercase">Modelagens</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-mono font-bold text-primary/90">{store.totalCategories}</div>
                                                            <div className="text-[10px] text-primary/40 uppercase">Categorias</div>
                                                        </div>
                                                        <div>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); if (store.totalAccesses > 0) openAccessDetail(store.members.map(m => m.client_id)) }}
                                                                className={`text-sm font-mono font-bold text-primary/90 hover:text-primary hover:underline ${store.totalAccesses > 0 ? "cursor-pointer" : "cursor-default"}`}
                                                            >
                                                                {store.totalAccesses}
                                                            </button>
                                                            <div className="text-[10px] text-primary/40 uppercase">Acessos</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {!isSingle && isExpanded && (
                                                    <div className="border-t border-white/10">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="text-primary/40 text-xs uppercase">
                                                                    <th className="text-left px-4 py-1.5 pl-11 font-medium">Usuario</th>
                                                                    <th className="text-left px-4 py-1.5 font-medium">Cargo</th>
                                                                    <th className="text-right px-4 py-1.5 font-medium">Projetos</th>
                                                                    <th className="text-right px-4 py-1.5 font-medium">Modelagens</th>
                                                                    <th className="text-right px-4 py-1.5 font-medium">Categorias</th>
                                                                    <th className="text-right px-4 py-1.5 font-medium">Acessos</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {store.members.map((m) => (
                                                                    <tr key={m.client_id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                                                                        <td className="px-4 py-1.5 pl-11 font-medium text-primary/80">{m.name}</td>
                                                                        <td className="px-4 py-1.5">
                                                                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                                                                m.role === "designer" ? "bg-blue-500/20 text-blue-400" :
                                                                                m.role === "vendedor" ? "bg-green-500/20 text-green-400" :
                                                                                m.role === "gestao" ? "bg-purple-500/20 text-purple-400" :
                                                                                "bg-white/10 text-primary/50"
                                                                            }`}>
                                                                                {m.role || "\u2014"}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-4 py-1.5 text-right font-mono text-primary/70">{m.projects}</td>
                                                                        <td className="px-4 py-1.5 text-right font-mono text-primary/70">{m.modelagens}</td>
                                                                        <td className="px-4 py-1.5 text-right font-mono text-primary/70">{m.categories}</td>
                                                                        <td className="px-4 py-1.5 text-right font-mono text-primary/70">{m.accesses}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                    {filtered.length === 0 && (
                                        <p className="text-center text-primary/40 py-8 text-sm">Nenhum resultado encontrado</p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>

        {/* Modal de detalhes de acessos */}
        <Dialog open={accessDetailOpen} onOpenChange={setAccessDetailOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col bg-background text-primary">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Detalhes dos Acessos
                    </DialogTitle>
                </DialogHeader>

                {accessDetailLoading ? (
                    <div className="flex-1 flex items-center justify-center py-12 text-primary/50">Carregando...</div>
                ) : (
                    <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                        <p className="text-sm text-primary/60">{accessDetails.length} acessos registrados</p>

                        {locationBreakdown.length > 0 && (
                            <div className="border border-white/10 rounded-lg overflow-hidden">
                                <div className="px-4 py-2 border-b border-white/10 bg-white/5">
                                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider">Por Localizacao</p>
                                </div>
                                <div className="divide-y divide-white/5">
                                    {locationBreakdown.map((loc, i) => (
                                        <div key={i} className="flex items-center justify-between px-4 py-2">
                                            <div>
                                                <div className="text-sm font-medium text-primary/90">
                                                    {[loc.city, loc.region, loc.country].filter(Boolean).join(", ") || "Desconhecido"}
                                                </div>
                                            </div>
                                            <span className="text-sm font-mono font-bold text-primary/70 bg-white/10 rounded-full px-2.5 py-0.5">
                                                {loc.count}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="border border-white/10 rounded-lg overflow-hidden">
                            <div className="px-4 py-2 border-b border-white/10 bg-white/5">
                                <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider">Historico (ultimos 500)</p>
                            </div>
                            <div className="divide-y divide-white/5 max-h-60 overflow-y-auto">
                                {accessDetails.map((d, i) => (
                                    <div key={i} className="flex items-center justify-between px-4 py-1.5 text-xs">
                                        <span className="text-primary/50 font-mono">
                                            {new Date(d.accessed_at).toLocaleString("pt-BR")}
                                        </span>
                                        <span className="text-primary/70">
                                            {[d.city, d.region, d.country].filter(Boolean).join(", ") || "\u2014"}
                                        </span>
                                    </div>
                                ))}
                                {accessDetails.length === 0 && (
                                    <p className="text-center text-primary/40 py-6">Nenhum acesso registrado</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
        </>
    )
}

function SummaryCard({ icon, label, value, onClick }: { icon: React.ReactNode; label: string; value: number; onClick?: () => void }) {
    return (
        <div className={`border border-white/10 rounded-lg p-3 ${onClick ? "cursor-pointer hover:bg-white/5" : ""}`} onClick={onClick}>
            <div className="flex items-center gap-2 text-primary/50 mb-1">
                {icon}
                <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
            </div>
            <p className="text-2xl font-bold text-primary/90">{value}</p>
        </div>
    )
}
